import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { useProjects } from "@/hooks/useProjects";
import { PROJECT_STATUS_TONE } from "@/lib/badgeTones";
import type { ProjectStatus } from "@/api/projects";

/**
 * Цвет точки берётся из `PROJECT_STATUS_TONE` (источник истины для оттенков
 * статусов) через handwritten-класс `.tone-{tone}-solid` (`surfaces.css`) —
 * saturated solid фон тона. `completed` намеренно приглушён (опускаем до
 * muted), как «ушёл в архив».
 */
const ROW_STATUSES: Array<{ status: ProjectStatus; dot: string; tone: string }> = [
  {
    status: "in_progress",
    dot: `tone-${PROJECT_STATUS_TONE.in_progress.tone}-solid`,
    tone: "text-foreground font-medium",
  },
  {
    status: "planned",
    dot: `tone-${PROJECT_STATUS_TONE.planned.tone}-solid`,
    tone: "text-foreground",
  },
  { status: "completed", dot: "bg-muted-foreground/40", tone: "text-muted-foreground" },
];

export function PortfolioOverviewWidget(): React.ReactElement {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useProjects({ limit: 200 });

  const counts = useMemo(() => {
    const buckets: Record<ProjectStatus, number> = {
      planned: 0,
      in_progress: 0,
      on_hold: 0,
      completed: 0,
      cancelled: 0,
    };
    for (const p of data ?? []) buckets[p.status] = (buckets[p.status] ?? 0) + 1;
    return buckets;
  }, [data]);

  return (
    <Card data-testid="widget-portfolio-overview">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">
          {t("home.widgets.portfolioOverview.title")}
        </CardTitle>
        <span className="text-2xl font-semibold tabular-nums">{counts.in_progress}</span>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
        ) : isError ? (
          <ApiErrorNotice
            error={error}
            fallbackMessage={t("common.apiError.fallback")}
            className="text-destructive text-sm"
          />
        ) : (
          <>
            <p className="text-muted-foreground mb-3 text-xs">
              {t("home.widgets.portfolioOverview.activeBig")}
            </p>
            <ul className="space-y-1.5 text-sm">
              {ROW_STATUSES.map(({ status, dot, tone }) => (
                <li key={status}>
                  <Link
                    to="/projects"
                    search={{ status }}
                    data-testid={`widget-portfolio-row-${status}`}
                    className={`hover:bg-accent flex items-center justify-between rounded px-1.5 py-1 ${tone}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${dot}`} />
                      {t(`home.widgets.portfolioOverview.rows.${status}`)}
                    </span>
                    <span className="tabular-nums">{counts[status]}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}
